import Text from '@components/Text';
import styled from '@emotion/styled';
import XLine from '@icons/XLine';
import {
    Fragment, ReactNode, useLayoutEffect, useRef, useState, MouseEvent,
} from 'react';

const MULTILINE_MAX_WIDTH = 290;
/** multiline 툴팁이 화면 오른쪽 끝과 띄울 최소 여백 */
const SCREEN_GUTTER = 16;

export interface Props {
    children?: ReactNode;
    className?:string;
    top?: number | string;
    bottom?: number | string;
    right?: number | string;
    left?: number| string;
    /** 지정하면 닫은 뒤 localStorage 에 저장해 다시 노출하지 않는다. */
    visibleKey?: string;
    /** 말풍선 꼬리 위치 */
    align?: 'left' | 'right' | 'center';
    hideClose?: boolean;
    onClose?: () => void;
    /** 최대 너비 안에서 자동 줄바꿈 */
    multiline?: boolean;
}

function Tooltip({
    children, className, top = 16, right = 20, bottom, left,
    visibleKey, align = 'center', hideClose, onClose, multiline,
}:Props) {
    const fullKey = visibleKey && `tooltip-${visibleKey}`;
    const [isHidden, setIsHidden] = useState<boolean>(typeof window !== 'undefined' && !!fullKey && !!localStorage.getItem(fullKey));
    const handleHide = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof window !== 'undefined' && fullKey) {
            window.localStorage.setItem(fullKey, 'true');
        }
        setIsHidden(true);
        onClose?.();
    };
    const contentsRef = useRef<HTMLDivElement>(null);
    const [maxWidth, setMaxWidth] = useState<number>();
    useLayoutEffect(() => {
        if (!multiline) return undefined;
        const updateMaxWidth = () => {
            if (!contentsRef.current) return;
            const { left: contentsLeft } = contentsRef.current.getBoundingClientRect();
            const available = document.documentElement.clientWidth - contentsLeft - SCREEN_GUTTER;
            setMaxWidth(Math.min(MULTILINE_MAX_WIDTH, available));
        };
        updateMaxWidth();
        window.addEventListener('resize', updateMaxWidth);
        return () => window.removeEventListener('resize', updateMaxWidth);
    }, [multiline]);
    if (isHidden) {
        return (<Fragment/>);
    }
    return (
        <Wrapper className={className}
            style={{
                top, right, bottom, left,
            }} >
            <Container align={align} multiline={multiline}>
                <Contents ref={contentsRef}
                    multiline={multiline}
                    style={{ maxWidth }}>
                    <TooltipText fontWeight='medium'
                        size='body-long'
                        as={multiline ? 'div' : 'p'}
                        multiline={multiline}>
                        {children}
                    </TooltipText>
                    {(!hideClose) && (
                        <CloseButton type='button'
                            aria-label='close'
                            onClick={handleHide}>
                            <XLine size={16}
                                color='var(--color-icon-unselected)'/>
                        </CloseButton>
                    )}
                </Contents>
                <Arrow width="30" height="12" viewBox="0 0 30 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 12L8 0L22 0L15 12Z" fill="var(--color-background-pure)"/>
                </Arrow>
            </Container>
        </Wrapper>
    );
}

function TooltipWrapper({ children }:{children?:ReactNode}) {
    return (
        <RelativeWrapper>
            {children}
        </RelativeWrapper>
    );
}

const ALIGN_ITEMS = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
} as const;

const Wrapper = styled.div`
    position: absolute;
    z-index: 2;
`;

const RelativeWrapper = styled.div`
    position: relative;
`;

const Container = styled('div', {
    shouldForwardProp: (prop) => prop !== 'align' && prop !== 'multiline',
})<{
    align: 'left' | 'right' | 'center';
    multiline?: boolean;
}>`
    align-items: ${({ align }) => ALIGN_ITEMS[align]};
    display: flex;
    filter: drop-shadow(0 6px 5px rgba(0, 0, 0, 0.05));
    flex-direction: column;
    white-space: ${({ multiline }) => (multiline ? 'normal' : 'nowrap')};
`;

const Contents = styled('div', {
    shouldForwardProp: (prop) => prop !== 'multiline',
})<{
    multiline?: boolean;
}>`
    align-items: center;
    background-color: var(--color-background-pure);
    border-radius: var(--radius-xs);
    column-gap: var(--spacing-1);
    display: flex;
    max-width: ${({ multiline }) => (multiline ? `${MULTILINE_MAX_WIDTH}px` : '80vw')};
    padding: var(--spacing-2) var(--spacing-4);
    ${({ multiline }) => multiline && 'width: max-content;'}
`;

const TooltipText = styled(Text)`
    ${({ multiline }) => !multiline && 'white-space: pre;'}
    word-break: break-word;
`;

const CloseButton = styled.button`
    background: none;
    display: flex;
    flex-shrink: 0;
    padding: 0;
`;

const Arrow = styled.svg`
    display: block;
    flex-shrink: 0;
`;

Tooltip.Wrapper = TooltipWrapper;

export default Tooltip;
